// How stored files are addressed and served — no AWS, no environment, safe in a
// browser. `@auger/storage/rules` is the entry the frontend app and the MCP
// import; everything that needs the SDK or credentials is in the package root.
// Neither entry depends on `@auger/core`, and core does not depend on this package.

/** The CloudFront host every stored file is served from. The CSP in
 *  auger-web/netlify.toml names it too. */
export const ASSET_HOST = "assets.heavybit.com"

export const ASSET_ORIGIN = `https://${ASSET_HOST}`

/** The bucket the app uploads to; ASSET_HOST serves its objects behind signed
 *  cookies. Synced legacy content (podcasts/, website/) lives in another bucket
 *  that ASSET_HOST serves publicly. */
export const UPLOAD_BUCKET_NAME = "heavybit-auger-assets"

/** The served URL for an object key. */
export function assetUrl(key: string): string {
  return `${ASSET_ORIGIN}/${key}`
}

/** The object key behind a served URL, or null when `url` isn't one. */
export function assetKey(url: string): string | null {
  const prefix = `${ASSET_ORIGIN}/`
  return url.startsWith(prefix) ? url.slice(prefix.length) : null
}

/** How long the signed cookies that unlock ASSET_HOST last. A browser re-mints
 *  them before this runs out. */
export const ASSET_COOKIE_MAX_AGE_S = 24 * 60 * 60

/** The largest upload accepted: 500MB, for videos. */
export const MAX_UPLOAD_BYTES = 500 * 1024 * 1024

/** The MIME types an upload may have. */
export const ALLOWED_UPLOAD_TYPES: readonly string[] = [
  // Images
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/svg+xml",
  // Videos
  "video/mp4",
  "video/quicktime",
  "video/webm",
  "video/x-msvideo",
  // Audio
  "audio/mpeg",
  "audio/mp4",
  "audio/x-m4a",
  "audio/wav",
  "audio/x-wav",
  // Documents
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  // Spreadsheets
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  // Presentations
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/vnd.apple.keynote",
  // Text
  "text/plain",
  "text/csv",
]

/** Why an upload isn't allowed, or null when it is. `size` is optional — an
 *  unknown size isn't refused. Checked before presigning; a browser can check
 *  the same before asking. */
export function uploadError({ mimeType, size }: { mimeType: string; size?: number | null }): string | null {
  if (size && size > MAX_UPLOAD_BYTES) {
    return `File too large. Maximum size is ${MAX_UPLOAD_BYTES / 1024 / 1024}MB`
  }
  if (!ALLOWED_UPLOAD_TYPES.includes(mimeType)) return `File type not allowed: ${mimeType}`
  return null
}

/**
 * Why a client-sent record can't name an upload's folder, or null when it
 * can. objectKey puts the type and id in the key as its first two segments,
 * so a type must be one lowercase name (every type in the `types` table is)
 * and an id a positive integer: nothing with a "/", "..", "?" or "#" in it.
 * A missing type or id (null or undefined) is not an error; the file goes
 * under general/ as before. This checks the shape only. The folder grants
 * nothing (asset access is by record_attachments, and the asset cookies
 * cover every key), so a well-formed but wrong record only misfiles a file.
 */
export function uploadRecordError(
  record?: { type?: string | null; id?: number | string | null } | null
): string | null {
  const type = record?.type
  const id = record?.id
  if (type != null && !/^[a-z][a-z0-9_]*$/.test(type)) return `Invalid record type: ${type}`
  if (id != null && !(typeof id === "number" ? Number.isSafeInteger(id) && id > 0 : /^[1-9][0-9]*$/.test(id))) {
    return `Invalid record id: ${id}`
  }
  return null
}

/**
 * Where a new object goes: `<type>/<id>/<ms>_<rand>_<filename>` under a record,
 * `general/<ms>_<rand>_<filename>` otherwise. The filename keeps only
 * `[a-zA-Z0-9._-]`; the timestamp and random part keep keys unique. `now` and
 * `rand` are for tests.
 */
export function objectKey({
  filename,
  record,
  now = Date.now,
  rand = () => Math.random().toString(36).substring(2, 10),
}: {
  filename: string
  record?: { type?: string | null; id?: number | string | null } | null
  now?: () => number
  rand?: () => string
}): string {
  const name = `${now()}_${rand()}_${filename.replace(/[^a-zA-Z0-9._-]/g, "_")}`
  return record?.type && record.id ? `${record.type}/${record.id}/${name}` : `general/${name}`
}

/** An object's S3 URL — what an asset row stores as `url`. Served through
 *  ASSET_HOST (assetUrl), not this. */
export function s3ObjectUrl(bucket: string, key: string): string {
  return `https://${bucket}.s3.amazonaws.com/${key}`
}

/** The S3 key an asset row records in its metadata, or null when it has none. */
export function assetS3Key(asset: { metadata?: unknown } | null | undefined): string | null {
  const key = (asset?.metadata as { s3_key?: unknown } | null | undefined)?.s3_key
  return typeof key === "string" && key ? key : null
}

/**
 * The URL a stored file is served from: its S3 key through ASSET_HOST, else the
 * row's own `url`. The canonical form, safe to persist.
 */
export function assetFileUrl(asset: { url?: string | null; metadata?: unknown } | null | undefined): string | null {
  if (!asset) return null
  const key = assetS3Key(asset)
  return key ? assetUrl(key) : asset.url || null
}

const EXTENSIONS: Record<string, string> = {
  "image/jpeg": ".jpg",
  "image/png": ".png",
  "image/gif": ".gif",
  "image/webp": ".webp",
  "image/svg+xml": ".svg",
  "application/pdf": ".pdf",
  "video/mp4": ".mp4",
}

/** A filename for a file fetched from `url`: the URL's own when it ends in an
 *  extension, else a timestamped name with the MIME type's extension. */
export function deriveFilename(url: string, mimeType: string, now: () => number = Date.now): string {
  const basename = new URL(url, "https://placeholder").pathname.split("/").pop()
  if (basename && /\.\w{2,5}$/.test(basename)) return basename
  return `upload_${now()}${EXTENSIONS[mimeType] ?? ""}`
}

/**
 * What the upload function answers with, as clients read it: `presignUpload`'s
 * result under the names the wire has always used.
 */
export interface PresignResponse {
  presignedUrl: string
  s3Key: string
  s3Url: string
  bucket: string
}

/** A presigned upload as the function sends it. */
export function presignResponse(upload: { putUrl: string; key: string; url: string; bucket: string }): PresignResponse {
  return { presignedUrl: upload.putUrl, s3Key: upload.key, s3Url: upload.url, bucket: upload.bucket }
}

/** Reads the function's answer back into a presigned upload; throws on a body
 *  that doesn't carry one. */
export function readPresignResponse(body: unknown): { putUrl: string; key: string; url: string; bucket: string } {
  const r = body as Partial<PresignResponse> | null | undefined
  if (!r?.presignedUrl || !r.s3Key || !r.s3Url || !r.bucket) {
    throw new Error("Invalid response from upload endpoint")
  }
  return { putUrl: r.presignedUrl, key: r.s3Key, url: r.s3Url, bucket: r.bucket }
}
